/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
/* eslint-disable camelcase */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getPipedCtrlLabelsMap, prepareFormForForm } from '../../../shared/form-lib';
import { fakeUpdateValueForRowContainers, assignAutofillValues } from './utils';
import { updateFormInventoryProducts } from '../../builder/slice-actions/form';
import { formatDateToReadable, prepareFormPayload } from '../utils';
import { Calculations } from '../../../shared/calculations_utils';

import { applyLogic } from '../../../shared/logic_utils';
import win from '../../../shared/win';
import {
  checkUnallowedPremiumFields,
  getEmptyTableFields,
  retrieveFromCookie,
  fplsFetch,
  getField,
} from '../../../shared/utils';
import { cloneDeep } from 'lodash';

export const stateType = {
  SUBMIT_ERROR: 0,
  NOT_FOUND: 2,
  WAS_ERROR: 3,
  LOADED: 4,
  SUBMITTING: 5,
  SUBMITTED: 6,
  NOT_VERIFIED: 7,
  TRIAL_EXPIRY: 8,
  QUOTA_EXCEEDED: 9,
  LINK_VISITED: 10,
  EDIT_EXPIRED: 11,
  ARCHIVED_FORM: 12,
  FORM_VIEW_EXCEEDED: 13,
  FILE_STORAGE_ERROR: 14,
  PREMIUM_FIELDS_ERROR: 15,
  SUBMISSION_START_ERROR: 16,
  FORBIDDEN: 17,
};

const UNIQUE_SUBMISSIONS_IDS = [1645076009, 1616536001, 1643046007 /* test form */];

export const formDefaultState = {
  error: '',
  notFoundText: '',
  loading: false,
  form_id: window.FORM_ID,
  responseState: null,
  archivedForm: false,
  formviewExceeded: false,
  isBeta: false,
  betaFeedbackLink: window.BETA_FEEDBACK_FORM_ID,
  plan: win.plan,
  userCustomTexts: {},
  initialResponse: {},
  hiddenFields: {},
  ctrlIndex: {},
  triggeringFields: {},
  actionFields: {},
  saveFormForLaterEmail: '',
  offLine: false,
  userIsOnline: true,
  hasFileUploadField: false,
  hasPaymentField: false,
  hasSignatureField: false,
  editingResponse: false,
  premium_fields: [],
  form: {},
  editableForm: {},
  oldForm: null,
  showIntroPage: false,
  reshowIntroPage: false,
  pageNotValid: false,
  isPayOnSubmission: false,
  eSign: {
    isLoading: false,
    openModal: false,
    error: '',
    auditTrail: {
      totalResponsePages: 0,
      form_owner_email: '',
      form_owner_name: '',
      form_name: '',
      signed: false,
      timezone: '',
      email: '',
      name: '',
      id: '',
      audit_trail: [
        {
          action_timestamp: '',
          action_type: '',
          ip_address: '',
          user_agent: ''
        }
      ]
    }
  },
  openPaymentModal: false,
  payments: {
    flutterwave: {},
    paystack: {},
    paypal: {},
    stripe: {},
  },
  phoneNumberFields: {},
  savedFormPaymentSubmissionId: null,
  uploadCount: 0,
  form_analytics: {
    form_id: 'FORM_ID',
    country: '',
    device: '',
    unique_visit: true,
    time_spent: 0,
    submitted: false,
    session_id: 0,
    browser: '',
    current_user: 'OWNER',
  },
  formGeolocation: {
    lat: '',
    lng: '',
  },
  respondentQuizTimerInfo: {
    quizId: '',
    timeElapsed: {},
  },
  columnHasCalculation: {},
  tableColumnResult: {},
  tableColumnCalculations: {},
  valuePipedCtrlLabelsMap: {},
  userAmount: {},
  visibleCaptcha: {
    checkCaptcha: false,
    captchaResponse: '',
  },
};

export const fetchFormById = createAsyncThunk(
  'form/fetchFormById',
  async ({ form_id, saved_response_key, edit_response_key }) => {
    const editResponseKey = edit_response_key ?? '';
    const savedResponseKey = saved_response_key ?? '';

    try {
      const response = await fplsFetch({
        url: `/api/v2/public/form/${form_id}?saved_response_key=${savedResponseKey}&edit_response_key=${editResponseKey}`,
        removeToken: true,
      });

      return await updateFormInventoryProducts(response, form_id, true);
    } catch (err) {
      console.error(err, 'error');
    }
  }
);

const formSlice = createSlice({
  name: 'form',
  initialState: formDefaultState,
  reducers: {
    isEditingResponse: (state, { payload }) => {
      state.editingResponse = payload;
    },
    setStateType: (state, { payload }) => {
      state.loading = false;
      state.responseState = payload;
    },
    updateOnlineStatus: (state, { payload }) => {
      state.userIsOnline = payload.online;
    },
    doReshowIntroPage: (state) => {
      state.reshowIntroPage = true;
    },
    hideIntroPage: (state) => {
      state.showIntroPage = false;
      state.reshowIntroPage = false;
    },
    setUserInputOnCtrl: (state, { payload }) => {
      const { pages: allPages = [], rules = [], calculations = [] } = state.editableForm?.definition ?? {};
      const visiblePages = allPages.filter((p) => !state.hiddenFields[p.internal_id]);
      const { newValue, property, page, ctrlId, isValid, tr, col } = payload;
      const { controls } = visiblePages[page];

      const ctrl = controls.find((ctr) => ctr.internal_id === ctrlId);
      if (ctrl.type === 'table_field') {
        if (!ctrl.value && newValue) {
          ctrl.value = getEmptyTableFields(ctrl.props.min_rows, ctrl.props.columns);
          ctrl.value[tr][col] = newValue;
          ctrl._validationError = getEmptyTableFields(ctrl.props.min_rows, ctrl.props.columns);
        } else if (!ctrl.value) {
          ctrl.value = getEmptyTableFields(ctrl.props.min_rows, ctrl.props.columns);
          ctrl._validationError = getEmptyTableFields(ctrl.props.min_rows, ctrl.props.columns);
        } else {
          ctrl.value[tr][col] = newValue;
        }
      } else {
        if (ctrl.props.is_inventory_payment && property !== 'value') {
          ctrl.props[property] = newValue;
        } else {
          ctrl[property] = newValue;
        }
        if (isValid) ctrl._validationError = null;
      }
      state.ctrlIndex[ctrlId] = ctrl;

      state.valuePipedCtrlLabelsMap = getPipedCtrlLabelsMap(allPages);

      // fake update values for row containers in visible pages
      // this is to ensure calculation/logic retriggers for row controls on page rerender
      if (calculations.length || rules.length) {
        fakeUpdateValueForRowContainers(visiblePages, calculations, rules);
      }
    },
    deleteTableRow: (state, { payload }) => {
      const { i, page, tableId } = payload;
      const { pages: allPages } = state.editableForm.definition;
      const visiblePages = allPages.filter((p) => !state.hiddenFields[p.internal_id]);
      const { controls } = visiblePages[page];
      const tableField = getField(controls, tableId);
      if (tableField) {
        if (!tableField.value) {
          tableField.value = getEmptyTableFields(tableField.props.min_rows, tableField.props.columns);
          tableField._validationError = getEmptyTableFields(tableField.props.min_rows, tableField.props.columns);
        } else if (!tableField._validationError) {
          tableField._validationError = getEmptyTableFields(tableField.value.length, tableField.value[0].length);
        }
        const tableValue = tableField.value;
        const tableValidationError = tableField._validationError;
        const filteredTableField = tableValue.filter((item) => item !== tableValue[i]);
        tableField.value = filteredTableField;
        const filteredTableValidation = tableValidationError.filter((item) => item !== tableValidationError[i]);
        tableField._validationError = filteredTableValidation;
      }
    },
    addTableRow: (state, { payload }) => {
      const { page, tableId } = payload;
      const { pages: allPages } = state.editableForm.definition;
      const visiblePages = allPages.filter((p) => !state.hiddenFields[p.internal_id]);
      const { controls } = visiblePages[page];
      const tableField = getField(controls, tableId);
      if (tableField) {
        if (!tableField.value) {
          tableField.value = getEmptyTableFields(tableField.props.min_rows, tableField.props.columns);
          tableField._validationError = getEmptyTableFields(tableField.props.min_rows, tableField.props.columns);
        } else if (!tableField._validationError) {
          tableField._validationError = getEmptyTableFields(tableField.props.min_rows, tableField.props.columns);
        }
        tableField.value = [...tableField.value, [...Array(tableField.props.columns).keys()].map(() => '')];
        tableField._validationError = [
          ...tableField._validationError,
          [...Array(tableField.props.columns).keys()].map(() => null),
        ];
      }
    },
    setAutoFillValues: (state, { payload }) => {
      const { autoFillValues } = payload;
      const { pages: allPages } = state.editableForm.definition;

      for (const page of allPages) {
        const pageControls = page.controls;
        for (const control of pageControls) {
          assignAutofillValues(autoFillValues, control);
          state.ctrlIndex[control.internal_id] = control;
        }
      }
    },
    updateFieldError: (state, { payload }) => {
      const { ctrls, pageIndex } = payload;
      const { pages: allPages } = state.editableForm.definition;
      const visiblePages = allPages.filter((p) => !state.hiddenFields[p.internal_id]);
      const { controls } = visiblePages[pageIndex];

      for (let i = 0; i < ctrls.length; i += 1) {
        const control = controls.find((ctr) => ctr.internal_id === ctrls[i].internal_id);
        if (control.type === 'table_field') {
          control.value = ctrls[i].value;
        }
        control._validationError = ctrls[i]._validationError;
      }
    },
    doPageApplyLogic: (state, { payload }) => {
      const { triggerableControls } = payload;
      const { ctrlIndex, hiddenFields, editableForm, triggeringFields, actionFields } = state;

      for (const ctrl of triggerableControls) {
        applyLogic(ctrl.internal_id, ctrlIndex, triggeringFields, actionFields, editableForm, hiddenFields);
      }
    },
    doPageApplyCalculations: (state, { payload }) => {
      const { calculationOperands } = payload;
      const { ctrlIndex, userAmount, editableForm, tableColumnResult, columnHasCalculation, tableColumnCalculations } = state;
      const { pages: allPages, calculations } = editableForm.definition;

      const newCtrIndex = cloneDeep(ctrlIndex);
      const newUserAmount = cloneDeep(userAmount);
      const newTableColumnResult = cloneDeep(tableColumnResult);
      const newColumnHasCalculation = cloneDeep(columnHasCalculation);
      const newTableColumnCalculations = cloneDeep(tableColumnCalculations);

      for (const ctrl of calculationOperands) {
        state.ctrlIndex[ctrl.internal_id] = ctrl;

        const calc = new Calculations({
          ctrl,
          calculations,
          ctrlIndex: newCtrIndex,
          userAmount: newUserAmount,
          tableColumnResult: newTableColumnResult,
          columnHasCalculation: newColumnHasCalculation,
          tableColumnCalculations: newTableColumnCalculations,
        }).apply();

        for (const page of allPages) {
          for (const control of page.controls) {
            const searchId = control.internal_id;
            const resultField = calc.resultFields[searchId];

            if (resultField) {
              control.value = resultField.value;
              state.ctrlIndex[searchId] = resultField;
            }
          }
        }
    
        state.userAmount = calc.userAmount;
        state.tableColumnResult = calc.tableColumnResult;
        state.tableColumnCalculations = calc.tableColumnCalculations;
      }

      state.valuePipedCtrlLabelsMap = getPipedCtrlLabelsMap(allPages);
    },
    confirmPayment: (state, action) => {
      const { payload } = action;
      const { type, isPaid, isError, message, ctrlId, transactionId } = payload;
      state.payments[type][ctrlId] = { isPaid, isError, message, transactionId };
    },
    doUpdatePhoneNumberData: (state, action) => {
      const { payload } = action;
      const { ctrlId, data } = payload;
      state.phoneNumberFields[ctrlId] = data;
    },
    updateFormAnalytics: (state, action) => {
      const { payload } = action;
      const { key, value } = payload;
      state.form_analytics[key] = value;
    },
    setFormAnalytics: (state, action) => {
      const { payload } = action;
      const { updatedAnalytics } = payload;
      state.form_analytics = updatedAnalytics;
    },
    updateFormGeolocation: (state, action) => {
      const { payload } = action;
      state.formGeolocation = { ...state.formGeolocation, ...payload };
    },
    startUpload: (state) => {
      state.uploadCount += 1;
    },
    endUpload: (state) => {
      state.uploadCount -= 1;
    },
    saveFormPaymentSubmissionId: (state, { payload }) => {
      const { response } = payload;
      state.savedFormPaymentSubmissionId = response.saved_payment_submission_id;
    },
    saveFormPaymentError: (state, { payload }) => {
      const { response } = payload;
      state.saveFormPaymentError = response.error;
    },
    updateVisibleCaptcha: (state, action) => {
      const { payload } = action;
      state.visibleCaptcha = { ...state.visibleCaptcha, ...payload };
    },
    updateRespondentEmail: (state, { payload }) => {
      state.editableForm.respondent_email = payload
    },
    togglePaymentModal: (state, { payload }) => {
      state.openPaymentModal = payload;
    },
    updateESign: (state, { payload }) => {
      state.eSign = {
        ...state.eSign,
        ...payload
      };
    },
    toggleESignModal: (state, { payload }) => {
      state.eSign.openModal = payload;
    },
    updateRespondentQuizTimerInfo: (state, { payload }) => {
      state.respondentQuizTimerInfo = { ...state.respondentQuizTimerInfo, ...payload };
    },
  },
  extraReducers: (form) => {
    form.addCase(fetchFormById.pending, (state) => {
      state.loading = true;
    });
    form.addCase(fetchFormById.fulfilled, (state, { payload }) => {
      if (!payload) return {
        ...state,
        loading: false,
        responseState: stateType.WAS_ERROR,
      };

      if (payload.error) {
        const isForbidden = String(payload.status) === '403';
        const isNotFound = String(payload.status) === '404';
        
        if (isNotFound) return {
          ...state,
          loading: false,
          responseState: stateType.NOT_FOUND,
        };

        if (isForbidden) return {
          ...state,
          loading: false,
          responseState: stateType.FORBIDDEN,
          notFoundText: payload.error,
        };

        return {
          ...state,
          loading: false,
          responseState: stateType.WAS_ERROR,
        };
      }

      if (!payload.verified) {
        console.error('EMAIL_VERIFICATION_ERROR');

        return {
          ...state,
          loading: false,
          responseState: stateType.NOT_VERIFIED,
        };
      }

      if (payload.exceeded) {
        console.error('SUBMISSION_QUOTA_EXCEEDED');

        return {
          ...state,
          loading: false,
          responseState: stateType.QUOTA_EXCEEDED,
        };
      }

      if (payload.unique_link_visited) {
        return {
          ...state,
          loading: false,
          responseState: stateType.LINK_VISITED,
        };
      }

      if (UNIQUE_SUBMISSIONS_IDS.indexOf(state.form_id) >= 0) {
        const hasSubmitted = retrieveFromCookie(state.form_id);

        if (hasSubmitted) {
          return {
            ...state,
            loading: false,
            responseState: stateType.LINK_VISITED,
          };
        }
      }

      // Update this for workFlow later abegg
      if (payload.form_options && state.editingResponse) {
        const current_date = Number(new Date());
        if (payload.form_options.edit_deadline) {
          let edit_deadline = Number(new Date(payload.form_options.edit_deadline));
          // add 23 hours 59 minutes
          edit_deadline += 86399000;
          if (current_date > edit_deadline) {
            return {
              ...state,
              loading: false,
              responseState: stateType.EDIT_EXPIRED,
            };
          }
        } else if (payload.form_options.stop_edit_after) {
          const { result_row_created } = payload;
          const stop_edit_after = payload.form_options.stop_edit_after * 86400000;
          const expiry_date = Number(new Date(result_row_created)) + stop_edit_after;
          if (current_date > expiry_date) {
            return {
              ...state,
              loading: false,
              responseState: stateType.EDIT_EXPIRED,
            };
          }
        }
      }

      if (payload.submission_start_date) {
        const today = new Date();
        const submissionStartDate = new Date(`${payload.submission_start_date}.000Z`);
        const readableSubmissionStartDate = formatDateToReadable(submissionStartDate);
        const isFormNowOpen = today >= submissionStartDate;

        // Check if the form has not yet opened
        if (!isFormNowOpen) {
          const errorMessage = `This form is not yet accepting responses and will become accessible on ${readableSubmissionStartDate} based on your time zone`;

          return {
            ...state,
            loading: false,
            responseState: stateType.SUBMISSION_START_ERROR,
            notFoundText: errorMessage,
          };
        }
      }

      if (payload.definition && payload.definition.pages) {
        // When form submission deadline reaches, there is no form, hence no form definition.
        // checkUnallowedPremiumFields can only check for premium fields in form definition.
        const unallowedPremiumFields = checkUnallowedPremiumFields(payload);
        
        if (unallowedPremiumFields.length >= 1) {
          console.error('PREMIUM_FIELDS_FOUND_IN_FREE');
          
          return {
            ...state,
            loading: false,
            unallowedPremiumFields,
            responseState: stateType.PREMIUM_FIELDS_ERROR,
          };
        }

        const { newPayload, initialResponse } = prepareFormPayload(payload);
        const {
          ctrlIndex,
          actionFields,
          hiddenFields,
          hasPaymentField,
          triggeringFields,
          hasSignatureField,
          tableColumnResult,
          hasFileUploadField,
          tableColumnCalculations,
          hasOfflineRestrictedRequiredField,
        } = prepareFormForForm(newPayload);

        const valuePipedCtrlLabelsMap = getPipedCtrlLabelsMap(newPayload.definition.pages);

        const { enable_timer, timer_options } = payload.form_options;

        const hasIntroMessage = Boolean(payload.form_options?.enable_intro_page && payload.intro_message);

        const requestQuizUniqueId = Boolean(
          enable_timer && timer_options?.timer_purpose === 'quiz' &&
          timer_options?.timer_unique_ids?.values?.length
        );

        const needsEmail = payload.collect_user_email && !payload.respondent_email;
        const needsPaymentEmail = hasPaymentField && !payload.respondent_email;

        const showIntroPage = Boolean(
          requestQuizUniqueId ||
          needsPaymentEmail ||
          hasIntroMessage ||
          needsEmail
        );

        const closingPages = payload.closing_pages;
        const userCustomTexts = payload.user_custom_texts;
        const editResponse = payload.form_options?.edit_response;
        const formAllowsOffline = payload.form_options?.offline_enabled;
        const hideSubmitButtton = payload.form_options?.hide_submit_button;
        const disableFileUpload = window.STORAGE_EXCEEDED && hasFileUploadField && payload.storage_service === 'formplus';

        if (hasFileUploadField && win.PLAN.allowed_storage_services.indexOf(payload.storage_service) < 0) {
          return {
            ...state,
            loading: false,
            payloadError: stateType.FILE_STORAGE_ERROR,
          };
        }

        window.addCaptchaLog(`SC: ${payload.show_captcha ? 'T' : 'F'}.`);

        return {
          ...state,
          ctrlIndex,
          actionFields,
          hiddenFields,
          closingPages,
          editResponse,
          showIntroPage,
          loading: false,
          hasIntroMessage,
          hasPaymentField,
          userCustomTexts,
          initialResponse,
          triggeringFields,
          form: newPayload,
          disableFileUpload,
          formAllowsOffline,
          tableColumnResult,
          hasSignatureField,
          hideSubmitButtton,
          hasFileUploadField,
          requestQuizUniqueId,
          valuePipedCtrlLabelsMap,
          tableColumnCalculations,
          editableForm: newPayload,
          responseState: stateType.LOADED,
          hasOfflineRestrictedRequiredField,
        };
      }

      return {
        ...state,
        loading: false,
        responseState: stateType.WAS_ERROR,
        oldForm: !payload.definition || pages == null,
      };
    });
    form.addCase(fetchFormById.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = payload;
    });
  },
});

export const {
  updateOnlineStatus,
  isEditingResponse,
  setStateType,
  hideIntroPage,
  doReshowIntroPage,
  setUserInputOnCtrl,
  deleteTableRow,
  addTableRow,
  setAutoFillValues,
  updateFieldError,
  openPaymentModal,
  updateESign,
  toggleESignModal,
  confirmPayment,
  updateFormAnalytics,
  setFormAnalytics,
  updateFormGeolocation,
  doPageApplyLogic,
  doPageApplyCalculations,
  saveFormPaymentSubmissionId,
  saveFormPaymentError,
  startUpload,
  endUpload,
  updateVisibleCaptcha,
  doUpdatePhoneNumberData,
  updateRespondentEmail,
  togglePaymentModal,
  updateRespondentQuizTimerInfo,
} = formSlice.actions;

export default formSlice.reducer;
